import { DAY_NAMES_BY_GET_DAY, fromIsoDate } from './recurringSeries';
import type { EventSeriesConflict, EventSeriesDeleteResult } from '../types/calendar';

/**
 * [magánóra-sorozat, 2026-10-31] Közös segédek a SZERVER-oldali sorozat-végpontokhoz
 * (`POST /api/events/series`, `…/{groupId}/extend`, `DELETE /api/events/series/{groupId}?fromDate=`).
 * A létrehozó (`EventCreateDialog`), a szerkesztő (`EventEditDialog`) és a részletező (`EventDetailsDialog`)
 * ugyanezt használja — egy fogalom, egy hely.
 */

/**
 * A sorozat felső határa (klub-döntés 2026-10-31; a szerver kényszeríti: `RecurringSeriesDates.MaxOccurrences`).
 * Létrehozásnál a teljes sorozatra, bővítésnél EGY bővítő hívásra vonatkozik.
 */
export const MAX_SERIES_OCCURRENCES = 26;

/** Magyar napnév → JS `getDay()` szám (0 = vasárnap … 6 = szombat), növekvő sorrendben, ismétlés nélkül. */
export function dayNamesToNumbers(dayNames: string[]): number[] {
  return Array.from(new Set(dayNames.map(n => DAY_NAMES_BY_GET_DAY.indexOf(n)).filter(n => n >= 0))).sort((a, b) => a - b);
}

/** `2026-11-03` → `2026-11-03 (kedd)` — a felhasználó a hét napját is lássa (több hétnapos sorozatnál fontos). */
export function formatSeriesDay(iso: string): string {
  const day = String(iso).slice(0, 10);
  const d = fromIsoDate(day);
  const name = DAY_NAMES_BY_GET_DAY[d.getDay()];
  return name ? `${day} (${name.toLowerCase()})` : day;
}

/**
 * Az ütköző napok a hibás (400) válasz törzséből. A szerver minden-vagy-semmi: ütközésnél
 * `{ message, conflicts: [{ eventDate, reason }] }`, és semmi nem jön létre. Típusos olvasás: ismeretlen
 * alakra üres lista (a hibaüzenet ettől még a közös `saveErrorMessage`-ből megy ki).
 */
export function seriesConflictsFromError(err: unknown): EventSeriesConflict[] {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  const list = data && typeof data === 'object' ? (data as { conflicts?: unknown }).conflicts : undefined;
  if (!Array.isArray(list)) return [];
  return list
    .filter((c): c is { eventDate: unknown; reason?: unknown } => !!c && typeof c === 'object' && 'eventDate' in c)
    .map(c => ({ eventDate: String(c.eventDate).slice(0, 10), reason: typeof c.reason === 'string' ? c.reason : '' }));
}

/** „Ütközés: <nap (ok)>, … — így a sorozat nem hozható létre" */
export function describeSeriesConflicts(conflicts: EventSeriesConflict[]): string {
  const items = conflicts.map(c => `${formatSeriesDay(c.eventDate)}${c.reason ? ` (${c.reason})` : ''}`);
  return `Ütközés: ${items.join('; ')} — így a sorozat nem hozható létre.`;
}

/**
 * „Saját sorozat": a csoport MINDEN (a hívó által látható) tagját ő hozta létre. A szerver a mérvadó (ő az
 * inaktív és a nem látható tagokat is nézi, `EventController.cs` `ExtendEventSeries` / `DeleteEventSeries`
 * tulajdon-őre) — ez csak a gomb megjelenítéséről dönt. Üres lista vagy hiányzó `createdBy` → NEM saját.
 */
export function isSeriesOwnedBy(members: Array<{ createdBy?: string | null }>, userId: string | undefined | null): boolean {
  if (!userId || members.length === 0) return false;
  const me = userId.toLowerCase();
  return members.every(m => typeof m.createdBy === 'string' && m.createdBy.toLowerCase() === me);
}

// [magánóra-sorozat, 2026-10-31, 1. web-validátor F2] A `past` NEM „már megtartott": a szerver a már ELKEZDŐDÖTT
// (folyamatban lévő) mai alkalmat is `past`-ként hagyja meg (`EventController.cs` `DeleteEventSeries`: „már elmúlt
// vagy folyamatban van"), arról pedig nem tudjuk, megtartották-e. „Már megtartott" csak a rögzített jelenlét.
const KEPT_REASON_LABELS: Array<[codes: string[], label: string]> = [
  [['past'], 'már elmúlt vagy folyamatban van'],
  [['attendance'], 'már megtartott'],
  [['paid'], 'kifizetett'],
  [['reassigned'], 'másik edzőhöz került'],
];

/**
 * A sorozat-törlés visszajelzése: „A sorozat hátralévő N alkalma törölve; M alkalom megmaradt (kifizetett /
 * már megtartott)". Az okok a `kept` `reasonCode`-jaiból (past → „már elmúlt vagy folyamatban van", attendance →
 * „már megtartott", paid → „kifizetett", reassigned → „másik edzőhöz került"); ismeretlen kódnál a szerver mondata.
 */
export function describeSeriesDeleteResult(result: EventSeriesDeleteResult): string {
  const deleted = result.deletedCount ?? result.deleted?.length ?? 0;
  const kept = result.kept ?? [];
  const lead = deleted > 0
    ? `A sorozat hátralévő ${deleted} alkalma törölve`
    : 'A sorozatból egy alkalom sem törlődött';
  if (kept.length === 0) return `${lead}.`;
  const labels: string[] = [];
  for (const [codes, label] of KEPT_REASON_LABELS) {
    if (kept.some(k => codes.includes(k.reasonCode))) labels.push(label);
  }
  const known = KEPT_REASON_LABELS.flatMap(([codes]) => codes);
  for (const k of kept) {
    if (!known.includes(k.reasonCode) && k.reason && !labels.includes(k.reason)) labels.push(k.reason);
  }
  return `${lead}; ${kept.length} alkalom megmaradt (${labels.join(' / ')}).`;
}
